/**
 * InvoiceView.js
 *
 * Finance → Invoices page.
 *
 * What this page does:
 *   - Shows a table of all invoices (SALES and PURCHASE).
 *   - "Add Invoice" button opens a dialog form to create a new invoice.
 *   - When an invoice is saved, the backend automatically posts a ledger entry
 *     to the stakeholder's account (see InvoiceServiceImpl.java).
 *   - Each CONFIRMED invoice has a "Cancel" button that reverses the account entry.
 *   - Filter bar lets the user narrow down by type, stakeholder, status, date range.
 *
 * Layout:
 *   MainCard
 *     ├── Toolbar  (title, Add button, filter toggle)
 *     ├── Filter bar  (collapsible)
 *     ├── Invoice table  (sortable, paginated)
 *     └── AddInvoiceDialog  (modal form)
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import BlockIcon from "@mui/icons-material/Block";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import { visuallyHidden } from "@mui/utils";
import toast from "react-hot-toast";
import MainCard from "../../../components/MainCard";
import {
  cancelInvoiceApi,
  createInvoiceApi,
  filterInvoicesApi,
  getAllInvoicesApi,
} from "../../../components/api/InvoiceApiService";
import { retrieveFastStakeholdersApi } from "../../../components/api/StakeholderApiService";
// FX rate API — used to auto-fill the exchange rate from stored historical rates
import { getAllFxRatesApi, getRateForDateApi } from "../../../components/api/FxRateApiService";
// Shipping Instruction API — the "fast" endpoint returns a lightweight projection
// (no heavy nested entities re-fetched per keystroke), the same call already used
// for searchable SI pickers elsewhere in the app (TradeView, ClaimsReportView, etc.).
// We load it once when the dialog opens and filter/search entirely client-side,
// so typing in the search box never triggers extra network requests (fast + safe).
import { retrieveFastShippingInstructionsApi } from "../../../components/api/ShippingInstructionApiService";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Invoice types the system supports.
 * SALES    → we issue an invoice to a customer  (debits their AR account)
 * PURCHASE → we receive an invoice from a vendor (credits our AP account)
 */
const INVOICE_TYPES = [
  { value: "SALES",    label: "Sales (issued to customer)" },
  { value: "PURCHASE", label: "Purchase (received from vendor)" },
];

/**
 * Common currencies. Extend this list as needed.
 */
const CURRENCIES = ["USD", "EUR", "SGD", "IDR", "VND", "GBP", "TRY"];

/**
 * Maps a currency code to the field name stored in the FX rate database record.
 * All stored rates express how many units of that currency equal 1 USD:
 *   e.g. usdEur = 0.92  means  1 USD = 0.92 EUR
 * To get the invoice direction (how many USD = 1 EUR): fxRateToUsd = 1 / usdEur
 *
 * Currencies NOT in this map (GBP, TRY, etc.) have no stored rate →
 * the form shows a warning and lets the user type the rate manually.
 */
const CURRENCY_FIELD_MAP = {
  EUR: "usdEur",
  SGD: "usdSgd",
  IDR: "usdIdr",
  VND: "usdVnd",
  BRL: "usdBrl",
};

// Empty form state — used when opening the Add dialog fresh
const EMPTY_FORM = {
  invoiceNumber: "",
  invoiceType: "SALES",     // default to SALES so the stakeholder dropdown pre-filters to customers
  postingSide: "DEBIT",      // default follows invoice type; user may change it
  stakeholderId: "",
  shippingInstructionId: "", // optional link to a Shipping Instruction (relatedShippingInstructionId on save)
  invoiceDate: "",
  paymentDueDate: "",
  amount: "",
  vatRatePercent: "",
  currency: "USD",
  fxRateToUsd: "1",         // defaults to 1; user changes this if currency ≠ USD
  hufExchangeRate: "",
  description: "",
  notes: "",
};

// ─────────────────────────────────────────────────────────────────────────────
// Small helper components
// ─────────────────────────────────────────────────────────────────────────────

/** Colored chip for invoice type */
function TypeChip({ type }) {
  return (
    <Chip
      label={type === "SALES" ? "Sales" : "Purchase"}
      size="small"
      color={type === "SALES" ? "primary" : "secondary"}
      variant="outlined"
      sx={{ fontSize: "0.7rem" }}
    />
  );
}

/** Colored chip for invoice status */
function StatusChip({ status }) {
  return (
    <Chip
      label={status}
      size="small"
      color={status === "CONFIRMED" ? "success" : "error"}
      variant="outlined"
      sx={{ fontSize: "0.7rem" }}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Number / date formatters
// ─────────────────────────────────────────────────────────────────────────────

const fmtUsd = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtAmt = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 5 });
const fmtHuf = new Intl.NumberFormat("hu-HU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function fmt(val)    { return val == null ? "—" : fmtUsd.format(Number(val)); }
function fmtO(val)   { return val == null ? "—" : fmtAmt.format(Number(val)); }
function fmtH(val)   { return val == null ? "—" : fmtHuf.format(Number(val)); }
function fmtDate(v)  { return v ? new Date(v).toLocaleDateString("en-GB") : "—"; }
function fmtSiLink(row) {
  if (!row) return "—";
  const rawId = row.relatedShippingInstructionId ?? row.shippingInstructionId;
  const finalNumber = row.finalShippingInstructionNumber || row.shippingInstructionNumber || "";
  if (rawId == null && !finalNumber) return "—";
  return `${rawId ?? "—"}${finalNumber ? ` / ${finalNumber}` : ""}`;
}

/**
 * Builds the searchable label for one Shipping Instruction option in the
 * Add Invoice dialog's SI picker.
 *
 * Includes ID, SI number, and SI type in a single string so MUI's default
 * Autocomplete text filter (which matches substrings of this label) lets the
 * user search by any of the three without any custom filter function or
 * extra network round-trip.
 *
 * Falls back gracefully (never throws) if a field is missing — this keeps
 * the picker safe against incomplete/legacy SI records.
 */
function siOptionLabel(si) {
  if (!si) return "";
  const number = si.finalShippingInstructionNumber || si.shippingInstructionNumber || "—";
  const type = si.shippingInstructionType || "—";
  return `#${si.id} — ${number} (${type})`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sorting helper
// ─────────────────────────────────────────────────────────────────────────────

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => (b[orderBy] < a[orderBy] ? -1 : b[orderBy] > a[orderBy] ? 1 : 0)
    : (a, b) => (a[orderBy] < b[orderBy] ? -1 : a[orderBy] > b[orderBy] ? 1 : 0);
}

// ─────────────────────────────────────────────────────────────────────────────
// Table columns definition
// ─────────────────────────────────────────────────────────────────────────────

const COLUMNS = [
  { id: "invoiceNumber",   label: "Invoice #",     align: "left"  },
  { id: "invoiceType",     label: "Type",          align: "center"},
  { id: "stakeholderName", label: "Stakeholder",   align: "left"  },
  { id: "shippingInstruction", label: "SI",         align: "left"  },
  { id: "invoiceDate",     label: "Date",          align: "center"},
  { id: "paymentDueDate",  label: "Due Date",      align: "center"},
  { id: "amount",          label: "Amount",        align: "right" },
  { id: "currency",        label: "Currency",           align: "center"},
  { id: "amountUsd",       label: "Amount (USD)",  align: "right" },
  { id: "amountHuf",       label: "Amount (HUF)",  align: "right" },
  { id: "vatRatePercent",  label: "VAT %",         align: "right" },
  { id: "vatAmountHuf",    label: "VAT (HUF)",     align: "right" },
  { id: "description",     label: "Description",   align: "left"  },
  { id: "status",          label: "Status",        align: "center"},
  { id: "creator",         label: "Created By",    align: "left"  },
  { id: "actions",         label: "",              align: "center"},
];

// ─────────────────────────────────────────────────────────────────────────────
// Add Invoice Dialog
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Modal form for creating a new invoice.
 *
 * Props:
 *   open           – whether the dialog is visible
 *   onClose        – called when the user dismisses without saving
 *   onSaved        – called with the new Invoice after successful creation
 */
function AddInvoiceDialog({ open, onClose, onSaved }) {
  const [form, setForm]       = useState(EMPTY_FORM);
  const [saving, setSaving]   = useState(false);
  // Stakeholder dropdown options — re-fetched whenever invoiceType changes
  const [stakeholders, setStakeholders] = useState([]);
  // All historical FX rate records — loaded once on dialog open for auto-fill
  const [fxRates, setFxRates]           = useState([]);
  // Shipping Instruction picker options — loaded ONCE per dialog open (not on
  // every keystroke). The Autocomplete below then searches this array entirely
  // client-side, so it stays fast no matter how much the user types.
  const [shippingInstructions, setShippingInstructions] = useState([]);
  const [siLoading, setSiLoading] = useState(false);
  // Describes where the currently displayed FX rate came from:
  //   { date, rate } → auto-filled from a specific date's record
  //   "manual"       → currency has no stored rates; user must enter
  //   null           → USD (no conversion) or date not yet chosen
  const [fxRateSource, setFxRateSource] = useState(null);

  // On dialog open: reset everything and pre-load data
  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setFxRateSource(null);
      loadStakeholders("SALES"); // default type is SALES → load customers
      loadFxRates();
      loadShippingInstructions();
    }
  }, [open]);

  // Whenever the invoice date or currency changes, auto-fill the FX rate
  useEffect(() => {
    autoFillFxRate(form.invoiceDate, form.currency);
    // fxRates is a dependency so this re-runs once rates have loaded
  }, [form.invoiceDate, form.currency, fxRates]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load all historical FX rate records (once per dialog open)
  async function loadFxRates() {
    try {
      const { data } = await getAllFxRatesApi();
      setFxRates(Array.isArray(data) ? data : []);
    } catch {
      // Non-critical: FX rates just won’t auto-fill; user can type manually
    }
  }

  /**
   * Loads the Shipping Instruction picker list ONE time when the dialog opens.
   *
   * Safety notes:
   *   - Read-only GET call (fast-shipping-instructions) — cannot mutate data.
   *   - Passive/deleted SIs (recordStatus = PASSIVE) are filtered out below so
   *     the user can never link an invoice to a retired shipping instruction.
   *   - The dropdown is a MUI Autocomplete bound to real option objects, so the
   *     user can only ever pick an ID that actually exists in this list —
   *     there is no free-text path that could smuggle an arbitrary/invalid ID
   *     into the invoice payload.
   *   - Non-critical: if this call fails, the field is simply left empty/disabled
   *     and the rest of the invoice form still works (SI link stays optional).
   */
  async function loadShippingInstructions() {
    setSiLoading(true);
    try {
      const { data } = await retrieveFastShippingInstructionsApi();
      const active = (Array.isArray(data) ? data : []).filter(
        (si) => String(si?.recordStatus ?? "ACTIVE").toUpperCase() !== "PASSIVE"
      );
      setShippingInstructions(active);
    } catch {
      toast.error("Could not load shipping instructions. You can still save the invoice without linking one.");
    } finally {
      setSiLoading(false);
    }
  }

  /**
   * Looks up the stored FX rate for the given invoice date and currency,
   * then writes it into form.fxRateToUsd.
   *
   * Conversion logic:
   *   The database stores rates as “1 USD = X <currency>” (e.g. usdEur = 0.92).
   *   The invoice needs “1 <currency> = Y USD”, so Y = 1 / X.
   *
   *   Step 1: look in the locally-loaded fxRates array (no network call).
   *   Step 2: if nothing found, call GET /fx-rate-view/rate-for-date?date=…
   *           The backend checks its DB first, then falls back to the external
   *           MAS / BCB APIs server-side (API key never leaves the server).
   *           The result is also persisted by the backend for future requests.
   */
  async function autoFillFxRate(date, currency) {
    if (!currency) return;

    // USD needs no conversion
    if (currency === "USD") {
      setForm(prev => ({ ...prev, fxRateToUsd: "1" }));
      setFxRateSource(null);
      return;
    }

    const fieldName = CURRENCY_FIELD_MAP[currency];
    if (!fieldName) {
      // Currency exists in the system but has no stored FX rate
      setFxRateSource("manual");
      return;
    }

    if (!date) {
      setFxRateSource(null);
      return;
    }

    // ── Step 1: check locally-loaded rates (no network call) ──────────────
    if (fxRates.length > 0) {
      const target     = new Date(date);
      const candidates = fxRates
        .filter(r => r[fieldName] != null && new Date(r.rateDate) <= target)
        .sort((a, b) => new Date(b.rateDate) - new Date(a.rateDate));

      if (candidates.length > 0) {
        const record  = candidates[0];
        const usdToX  = Number(record[fieldName]);
        if (usdToX && usdToX > 0) {
          const xToUsd = (1 / usdToX).toFixed(8);
          setForm(prev => ({ ...prev, fxRateToUsd: xToUsd }));
          setFxRateSource({ date: record.rateDate, rate: xToUsd, fromApi: false });
          return;
        }
      }
    }

    // ── Step 2: not found locally — ask the backend (which calls the external
    //            API server-side if the date is not yet in the DB) ────────
    try {
      const { data } = await getRateForDateApi(date);
      const usdToX   = Number(data[fieldName]);          // e.g. data.usdEur
      if (usdToX && usdToX > 0) {
        const xToUsd = (1 / usdToX).toFixed(8);
        setForm(prev => ({ ...prev, fxRateToUsd: xToUsd }));
        setFxRateSource({ date: data.rateDate, rate: xToUsd, fromApi: true });
        // Add to local cache so subsequent changes within this dialog don't re-fetch
        setFxRates(prev => {
          const alreadyHas = prev.some(r => r.rateDate === data.rateDate);
          return alreadyHas ? prev : [...prev, data];
        });
      } else {
        setFxRateSource("not_found");
      }
    } catch {
      setFxRateSource("not_found");
    }
  }

  async function loadStakeholders(invoiceType) {
    try {
      const stakeholderType = invoiceType === "SALES" ? "CUSTOMER" : "VENDOR";
      const { data } = await retrieveFastStakeholdersApi(stakeholderType);
      setStakeholders(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Could not load stakeholders.");
    }
  }

  function handleChange(field, value) {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      // When invoice type changes, reload the stakeholder list and clear selection
      if (field === "invoiceType") {
        loadStakeholders(value);
        updated.stakeholderId = "";
        // Always reset to the invoice-type default so changing SALES <-> PURCHASE
        // never leaves the old side behind.
        updated.postingSide = value === "SALES" ? "DEBIT" : "CREDIT";
      }
      // Note: currency/date changes are handled by the autoFillFxRate useEffect above
      return updated;
    });
  }

  async function handleSave() {
    // ── Client-side validation ───────────────────────────────────────────────
    if (!form.invoiceNumber.trim()) return toast.error("Invoice number is required.");
    if (!form.stakeholderId)        return toast.error("Please select a stakeholder.");
    if (!form.invoiceDate)          return toast.error("Invoice date is required.");
    if (form.paymentDueDate && form.paymentDueDate < form.invoiceDate) {
      return toast.error("Payment due date cannot be before invoice date.");
    }
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Amount must be greater than zero.");
    if (form.vatRatePercent === "" || Number(form.vatRatePercent) < 0) {
      return toast.error("VAT rate must be zero or greater.");
    }
    if (!form.hufExchangeRate || Number(form.hufExchangeRate) <= 0) {
      return toast.error("HUF exchange rate must be greater than zero.");
    }
    if (!form.currency)             return toast.error("Currency is required.");

    const dto = {
      invoiceNumber:  form.invoiceNumber.trim(),
      invoiceType:    form.invoiceType,
      postingSide:    form.postingSide,
      stakeholderId:  Number(form.stakeholderId),
      invoiceDate:    form.invoiceDate,        // YYYY-MM-DD string
      paymentDueDate: form.paymentDueDate || null,
      amount:         Number(form.amount),
      vatRatePercent: Number(form.vatRatePercent) || 0,
      currency:       form.currency,
      fxRateToUsd:    Number(form.fxRateToUsd) || 1,
      hufExchangeRate: Number(form.hufExchangeRate),
      // fxRateDate is intentionally omitted here — the backend defaults it to invoiceDate
      description:    form.description.trim() || null,
      notes:          form.notes.trim()        || null,
      // Optional link to the Shipping Instruction chosen above. Guarded explicitly
      // (rather than `Number(form.shippingInstructionId) || null`) so an empty
      // selection sends null instead of accidentally sending 0.
      relatedShippingInstructionId: form.shippingInstructionId ? Number(form.shippingInstructionId) : null,
    };

    try {
      setSaving(true);
      const { data } = await createInvoiceApi(dto);
      toast.success("Invoice created and account updated.");
      onSaved(data); // pass the new invoice back to the parent to add to the table
    } catch (e) {
      // Show the backend validation message if available
      const msg = e?.response?.data?.message ?? e?.response?.data ?? "Failed to create invoice.";
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  }

  // Whether the FX rate field should be shown
  // (only meaningful when currency ≠ USD)
  const showFxRate = form.currency && form.currency !== "USD";
  const hufEquivalent = Number(form.amount || 0) * Number(form.hufExchangeRate || 0);
  const vatHufEquivalent = hufEquivalent * (Number(form.vatRatePercent || 0) / 100);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700} color="#1E2685">
            New Invoice
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>

          {/* Invoice Number */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" gutterBottom>Invoice Number *</Typography>
            <TextField
              size="small"
              fullWidth
              placeholder=""
              value={form.invoiceNumber}
              onChange={(e) => handleChange("invoiceNumber", e.target.value)}
            />
          </Grid>

          {/* Invoice Type — determines whether the stakeholder is a customer or vendor */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" gutterBottom>Invoice Type *</Typography>
            <TextField
              select
              size="small"
              fullWidth
              value={form.invoiceType}
              onChange={(e) => handleChange("invoiceType", e.target.value)}
            >
              {INVOICE_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="body2" gutterBottom>Posting Side *</Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={form.postingSide}
              onChange={(_, nextValue) => {
                if (!nextValue) return;
                handleChange("postingSide", nextValue);
              }}
              sx={{
                width: "100%",
                gap: 1,
                "& .MuiToggleButton-root": {
                  flex: 1,
                  borderRadius: 2,
                  borderColor: "divider",
                  py: 1,
                  fontWeight: 700,
                  letterSpacing: 0.2,
                  color: "text.secondary",
                  backgroundColor: "background.paper",
                },
                "& .MuiToggleButton-root.Mui-selected": {
                  color: "#fff",
                  backgroundColor: "#1E2685",
                  borderColor: "#1E2685",
                  boxShadow: "0 6px 16px rgba(30, 38, 133, 0.25)",
                },
                "& .MuiToggleButton-root.Mui-selected:hover": {
                  backgroundColor: "#17206b",
                },
              }}
            >
              <ToggleButton value="DEBIT">Debit</ToggleButton>
              <ToggleButton value="CREDIT">Credit</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" display="block">
              Default is {form.invoiceType === "SALES" ? "Debit" : "Credit"}, but you can change it.
            </Typography>
          </Grid>

          {/* Stakeholder — searchable autocomplete, filtered by invoice type */}
          <Grid item xs={12}>
            <Typography variant="body2" gutterBottom>
              Stakeholder * &nbsp;
              <Typography component="span" variant="caption" color="text.secondary">
                ({form.invoiceType === "SALES" ? "customers only" : "vendors only"})
              </Typography>
            </Typography>
            <Autocomplete
              options={stakeholders}
              // Display label shown in the dropdown and selected input
              getOptionLabel={(s) => s.code ? `[${s.code}] ${s.name}` : (s.name ?? "")}
              // Controlled value: find the full object by stored ID
              value={stakeholders.find(s => s.id === Number(form.stakeholderId)) ?? null}
              onChange={(_, newVal) => handleChange("stakeholderId", newVal?.id ?? "")}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              renderInput={(params) => (
                <TextField {...params} size="small" placeholder="Type name or code to search…" />
              )}
            />
          </Grid>

          {/* Shipping Instruction — OPTIONAL link between this invoice and an SI.
               Searchable by ID, SI number, and SI type all at once because those
               three fields are baked into getOptionLabel() below, and MUI's default
               Autocomplete filter matches substrings anywhere in that label.
               The list itself is fetched once (loadShippingInstructions, on dialog
               open) and searched entirely client-side, so typing never fires a
               network request and stays fast even with a large SI list. */}
          <Grid item xs={12}>
            <Typography variant="body2" gutterBottom>
              Shipping Instruction &nbsp;
              <Typography component="span" variant="caption" color="text.secondary">
                (optional — search by ID, SI number, or type)
              </Typography>
            </Typography>
            <Autocomplete
              options={shippingInstructions}
              loading={siLoading}
              getOptionLabel={siOptionLabel}
              value={shippingInstructions.find(si => si.id === Number(form.shippingInstructionId)) ?? null}
              onChange={(_, newVal) => handleChange("shippingInstructionId", newVal?.id ?? "")}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={siLoading ? "Loading shipping instructions…" : "Type ID, SI number, or type to search…"}
                />
              )}
            />
          </Grid>

          {/* Invoice Date — also used as the FX rate date on the backend */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" gutterBottom>Invoice Date *</Typography>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={form.invoiceDate}
              onChange={(e) => handleChange("invoiceDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Payment Due Date (optional) */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" gutterBottom>Payment Due Date</Typography>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={form.paymentDueDate}
              onChange={(e) => handleChange("paymentDueDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Currency */}
          <Grid item xs={12} sm={showFxRate ? 4 : 6}>
            <Typography variant="body2" gutterBottom>Currency *</Typography>
            <TextField
              select
              size="small"
              fullWidth
              value={form.currency}
              onChange={(e) => handleChange("currency", e.target.value)}
            >
              {CURRENCIES.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* FX Rate — only shown when currency ≠ USD.
               Auto-filled from the stored rate for the invoice date.
               User can override by typing directly. */}
          {showFxRate && (
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" gutterBottom>
                FX Rate ({form.currency}→USD)
              </Typography>
              <TextField
                type="number"
                size="small"
                fullWidth
                inputProps={{ min: 0, step: "0.0001" }}
                value={form.fxRateToUsd}
                onChange={(e) => handleChange("fxRateToUsd", e.target.value)}
              />
              {/* Source indicator — tells the user where the rate came from */}
              {fxRateSource && fxRateSource !== "manual" && fxRateSource !== "not_found" && (
                <Typography variant="caption" sx={{ color: "#2e7d32", mt: 0.4, display: "block" }}>
                  {fxRateSource.fromApi
                    ? `✓ Fetched from external API for ${fmtDate(fxRateSource.date)}`
                    : `✓ Auto-filled from ${fmtDate(fxRateSource.date)}`}
                </Typography>
              )}
              {(fxRateSource === "manual" || fxRateSource === "not_found") && (
                <Typography variant="caption" sx={{ color: "#e65100", mt: 0.4, display: "block" }}>
                  ⚠ No stored rate — enter manually
                </Typography>
              )}
            </Grid>
          )}

          {/* HUF rate — manually entered for now */}
          <Grid item xs={12} sm={showFxRate ? 4 : 6}>
            <Typography variant="body2" gutterBottom>HUF Exchange Rate *</Typography>
            <TextField
              type="number"
              size="small"
              fullWidth
              inputProps={{ min: 0, step: "0.0001" }}
              value={form.hufExchangeRate}
              onChange={(e) => handleChange("hufExchangeRate", e.target.value)}
              placeholder={`1 ${form.currency || "CCY"} = ? HUF`}
            />
          </Grid>

          {/* Amount */}
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" gutterBottom>Amount *</Typography>
            <TextField
              type="number"
              size="small"
              fullWidth
              inputProps={{ min: 0, step: "0.01" }}
              placeholder="0.00"
              value={form.amount}
              onChange={(e) => handleChange("amount", e.target.value)}
            />
          </Grid>

          {/* VAT rate */}
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" gutterBottom>VAT Rate (%) *</Typography>
            <TextField
              type="number"
              size="small"
              fullWidth
              inputProps={{ min: 0, step: "0.01" }}
              placeholder="15"
              value={form.vatRatePercent}
              onChange={(e) => handleChange("vatRatePercent", e.target.value)}
            />
          </Grid>

          {/* USD Preview — computed in real-time so the user can verify */}
          {form.amount && form.fxRateToUsd && (
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" gutterBottom>USD Equivalent</Typography>
              <Typography
                variant="body1"
                sx={{ mt: 0.8, fontWeight: 600, color: "#1E2685" }}
              >
                ${fmtUsd.format(Number(form.amount) * Number(form.fxRateToUsd))}
              </Typography>
            </Grid>
          )}

          {form.amount && form.hufExchangeRate && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" gutterBottom>HUF Equivalent</Typography>
              <Typography
                variant="body1"
                sx={{ mt: 0.8, fontWeight: 600, color: "#1565c0" }}
              >
                {fmtHuf.format(hufEquivalent)} HUF
              </Typography>
            </Grid>
          )}

          {form.amount && form.hufExchangeRate && form.vatRatePercent !== "" && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" gutterBottom>VAT from HUF Amount</Typography>
              <Typography
                variant="body1"
                sx={{ mt: 0.8, fontWeight: 700, color: "#2e7d32" }}
              >
                {fmtHuf.format(vatHufEquivalent)} HUF
              </Typography>
            </Grid>
          )}

          {/* Description (optional) */}
          <Grid item xs={12}>
            <Typography variant="body2" gutterBottom>Description (optional)</Typography>
            <TextField
              size="small"
              fullWidth
              multiline
              rows={2}
              placeholder="Short description shown on the invoice…"
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
            />
          </Grid>

          {/* Notes (optional internal remarks) */}
          <Grid item xs={12}>
            <Typography variant="body2" gutterBottom>Internal Notes (optional)</Typography>
            <TextField
              size="small"
              fullWidth
              multiline
              rows={2}
              placeholder="Internal remarks for operations staff…"
              value={form.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
            />
          </Grid>

        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          disableElevation
        >
          {saving ? "Saving…" : "Save Invoice"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page Component
// ─────────────────────────────────────────────────────────────────────────────

export default function InvoiceView() {
  // ── State ──────────────────────────────────────────────────────────────────

  const [rows, setRows]           = useState([]);   // all invoices currently loaded
  const [loading, setLoading]     = useState(false);
  const [addOpen, setAddOpen]     = useState(false); // controls Add dialog visibility

  // Table sorting
  const [order, setOrder]         = useState("desc");
  const [orderBy, setOrderBy]     = useState("invoiceDate");

  // Table pagination
  const [page, setPage]           = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filter bar visibility and filter values
  const [showFilters, setShowFilters] = useState(false);
  const [filter, setFilter]       = useState({
    invoiceNumber: "",
    invoiceType:   "",       // empty = all types
    status:        "",       // empty = all statuses
    invoiceDateFrom: "",
    invoiceDateTo:   "",
  });

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getAllInvoicesApi();
      // Flatten the nested stakeholder object for easier table rendering & sorting
      setRows((Array.isArray(data) ? data : []).map(flatten));
    } catch {
      toast.error("Failed to load invoices.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  /**
   * Flattens nested stakeholder fields onto the invoice row
   * so the table can sort/display them without extra lookups.
   */
  function flatten(inv) {
    return {
      ...inv,
      stakeholderName: inv.stakeholderName ?? inv.stakeholder?.name ?? "",
      stakeholderCode: inv.stakeholderCode ?? inv.stakeholder?.code ?? "",
    };
  }

  // ── Filter apply ───────────────────────────────────────────────────────────

  async function applyFilter() {
    try {
      setLoading(true);
      const dto = {
        invoiceNumber:   filter.invoiceNumber   || null,
        invoiceType:     filter.invoiceType     || null,
        status:          filter.status          || null,
        invoiceDateFrom: filter.invoiceDateFrom || null,
        invoiceDateTo:   filter.invoiceDateTo   || null,
      };
      const { data } = await filterInvoicesApi(dto);
      setRows((Array.isArray(data) ? data : []).map(flatten));
      setPage(0); // jump back to first page after filtering
    } catch {
      toast.error("Filter failed.");
    } finally {
      setLoading(false);
    }
  }

  function clearFilter() {
    setFilter({ invoiceNumber: "", invoiceType: "", status: "", invoiceDateFrom: "", invoiceDateTo: "" });
    fetchAll();
  }

  // ── Cancel invoice ─────────────────────────────────────────────────────────

  async function handleCancel(invoiceId, invoiceNumber) {
    if (!window.confirm(`Cancel invoice "${invoiceNumber}"?\nThis will reverse the account entry. This action cannot be undone.`))
      return;
    try {
      const { data } = await cancelInvoiceApi(invoiceId);
      // Update just the affected row in the table (no full reload needed)
      setRows((prev) => prev.map((r) => r.id === invoiceId ? flatten(data) : r));
      toast.success("Invoice cancelled and account entry reversed.");
    } catch (e) {
      const msg = e?.response?.data?.message ?? e?.response?.data ?? "Failed to cancel invoice.";
      toast.error(String(msg));
    }
  }

  // ── Add invoice callback ───────────────────────────────────────────────────

  function handleSaved(newInvoice) {
    // Prepend the new invoice to the top of the table
    setRows((prev) => [flatten(newInvoice), ...prev]);
    setAddOpen(false);
  }

  // ── Sorting ────────────────────────────────────────────────────────────────

  function handleSort(col) {
    setOrder((prev) => orderBy === col && prev === "asc" ? "desc" : "asc");
    setOrderBy(col);
  }

  const sorted = useMemo(
    () => [...rows].sort(getComparator(order, orderBy)),
    [rows, order, orderBy]
  );

  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <MainCard>

      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5, px: 1 }}
      >
        <Typography variant="h5" fontWeight={700} color="#1E2685">
          Invoices
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton size="small" onClick={fetchAll} disabled={loading} title="Refresh">
            <RefreshIcon />
          </IconButton>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters((v) => !v)}
          >
            {showFilters ? "Hide Filters" : "Filters"}
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            disableElevation
            onClick={() => setAddOpen(true)}
          >
            Add Invoice
          </Button>
        </Stack>
      </Stack>

      {/* ── Filter bar ──────────────────────────────────────────────────── */}
      <Collapse in={showFilters}>
        <Box sx={{ px: 1, pb: 2 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Grid container spacing={1.5} alignItems="flex-end">

            <Grid item xs={12} sm={3}>
              <Typography variant="caption">Invoice #</Typography>
              <TextField
                size="small"
                fullWidth
                placeholder="Partial match…"
                value={filter.invoiceNumber}
                onChange={(e) => setFilter((f) => ({ ...f, invoiceNumber: e.target.value }))}
              />
            </Grid>

            <Grid item xs={6} sm={2}>
              <Typography variant="caption">Type</Typography>
              <TextField
                select
                size="small"
                fullWidth
                value={filter.invoiceType}
                onChange={(e) => setFilter((f) => ({ ...f, invoiceType: e.target.value }))}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="SALES">Sales</MenuItem>
                <MenuItem value="PURCHASE">Purchase</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={6} sm={2}>
              <Typography variant="caption">Status</Typography>
              <TextField
                select
                size="small"
                fullWidth
                value={filter.status}
                onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="CONFIRMED">Confirmed</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={6} sm={2}>
              <Typography variant="caption">Date From</Typography>
              <TextField
                type="date"
                size="small"
                fullWidth
                value={filter.invoiceDateFrom}
                onChange={(e) => setFilter((f) => ({ ...f, invoiceDateFrom: e.target.value }))}
              />
            </Grid>

            <Grid item xs={6} sm={2}>
              <Typography variant="caption">Date To</Typography>
              <TextField
                type="date"
                size="small"
                fullWidth
                value={filter.invoiceDateTo}
                onChange={(e) => setFilter((f) => ({ ...f, invoiceDateTo: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12} sm={1}>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="contained" disableElevation onClick={applyFilter}>
                  Apply
                </Button>
                <Button size="small" variant="outlined" onClick={clearFilter}>
                  Clear
                </Button>
              </Stack>
            </Grid>

          </Grid>
        </Box>
      </Collapse>

      {/* ── Invoice Table ────────────────────────────────────────────────── */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align}
                  sx={{ fontWeight: 700, whiteSpace: "nowrap", backgroundColor: "#f5f5f5" }}
                >
                  {col.id !== "actions" ? (
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? order : "asc"}
                      onClick={() => handleSort(col.id)}
                    >
                      {col.label}
                      {orderBy === col.id && (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc" ? "sorted descending" : "sorted ascending"}
                        </Box>
                      )}
                    </TableSortLabel>
                  ) : col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">Loading…</Typography>
                </TableCell>
              </TableRow>
            ) : paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">No invoices found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ opacity: row.status === "CANCELLED" ? 0.55 : 1 }}
                >
                  {/* Invoice number — bold for quick scanning */}
                  <TableCell sx={{ fontWeight: 600 }}>{row.invoiceNumber}</TableCell>

                  <TableCell align="center">
                    <TypeChip type={row.invoiceType} />
                  </TableCell>

                  {/* Stakeholder name with code */}
                  <TableCell>
                    {row.stakeholderCode ? (
                      <Typography variant="body2">
                        <Typography component="span" variant="caption" color="text.secondary">
                          [{row.stakeholderCode}]{" "}
                        </Typography>
                        {row.stakeholderName}
                      </Typography>
                    ) : row.stakeholderName}
                  </TableCell>

                  <TableCell sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>
                    {row.relatedShippingInstructionId != null || row.finalShippingInstructionNumber ? (
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#1E2685" }}>
                        {fmtSiLink(row)}
                      </Typography>
                    ) : (
                      "—"
                    )}
                  </TableCell>

                  <TableCell align="center">{fmtDate(row.invoiceDate)}</TableCell>

                  <TableCell align="center">{fmtDate(row.paymentDueDate)}</TableCell>

                  {/* Original-currency amount */}
                  <TableCell align="right" sx={{ fontWeight: 500 }}>
                    {fmtO(row.amount)}
                  </TableCell>

                  <TableCell align="center">{row.currency}</TableCell>

                  {/* USD equivalent — always useful to see */}
                  <TableCell align="right" sx={{ fontWeight: 600, color: "#1E2685" }}>
                    ${fmt(row.amountUsd)}
                  </TableCell>

                  <TableCell align="right" sx={{ fontWeight: 600, color: "#1565c0" }}>
                    {fmtH(row.amountHuf)}
                  </TableCell>

                  <TableCell align="right">
                    {row.vatRatePercent != null ? fmtO(row.vatRatePercent) : "—"}
                  </TableCell>

                  <TableCell align="right" sx={{ fontWeight: 700, color: "#2e7d32" }}>
                    {fmtH(row.vatAmountHuf)}
                  </TableCell>

                  <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {row.description ?? "—"}
                  </TableCell>

                  <TableCell align="center">
                    <StatusChip status={row.status} />
                  </TableCell>

                  <TableCell>{row.creator ?? "—"}</TableCell>

                  {/* Cancel action — only available on CONFIRMED invoices */}
                  <TableCell align="center">
                    {row.status === "CONFIRMED" && (
                      <Tooltip title="Cancel invoice (reverses account entry)">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleCancel(row.id, row.invoiceNumber)}
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[25, 50, 100]}
        component="div"
        count={sorted.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      />

      {/* ── Add Invoice Dialog ───────────────────────────────────────────── */}
      <AddInvoiceDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={handleSaved}
      />

    </MainCard>
  );
}
